import { useState } from 'react';
import { PhoneIcon, EnvelopeIcon, MapPinIcon } from '@heroicons/react/24/outline';
import type { ParentRelationship, ParentSummary } from '../../types';
import {
  useAddStudentParentMutation,
  useListStudentParentsQuery,
  useRemoveStudentParentMutation,
  useUpdateStudentParentContactMutation,
} from '../../store/api/localApi';
import { useToast } from '../Toast';
import { LinkParentForm, describeLinkOutcome, messageFrom } from './LinkParentForm';

interface Props {
  studentId: string;
  /** First name for toast wording ("They'll see Emma…"). */
  childName: string;
  isStaffOrAbove: boolean;
  isAdmin: boolean;
}

/**
 * Who this child's parents are and how to reach them, on the StudentDetail
 * page. Staff and admins only — the endpoint 403s a PARENT caller, so the
 * query is skipped rather than merely hidden for a parent viewer.
 *
 * Admin (not plain STAFF) can also correct a parent's phone/address here —
 * their details were missing or wrong at registration. Email is never
 * editable: it's the sign-in identity. Editing is per-parent-ROW, not a
 * single global toggle, so a two-parent child has no ambiguity about which
 * parent is being corrected.
 *
 * Admin also links a further parent/guardian ("Add parent": email +
 * relationship, per child) and unlinks a co-parent. Unlinking is admin-only
 * everywhere — a parent may add another guardian from their own child page
 * but never remove one — and the primary parent has no Remove at all.
 *
 * This sits directly above ParentOnboardingCard because parent identity and
 * invite status belong together. The onboarding card does NOT cover this:
 * once registration completes it returns `{ stage: 'complete' }` and renders
 * nothing at all, which is exactly how staff ended up with no way to see a
 * parent's phone number.
 */
export function StudentParentsSection({ studentId, childName, isStaffOrAbove, isAdmin }: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const { data, isLoading } = useListStudentParentsQuery(studentId, { skip: !isStaffOrAbove });
  const [addParent] = useAddStudentParentMutation();
  const { showSuccess, showError } = useToast();

  if (!isStaffOrAbove) return null;

  const parents = data?.parents ?? [];

  const handleAdd = async (values: { email: string; relationship: ParentRelationship }) => {
    const { parent } = await addParent({ studentId, ...values }).unwrap();
    const outcome = describeLinkOutcome(parent, childName);
    (outcome.tone === 'success' ? showSuccess : showError)(outcome.message);
    setIsAdding(false);
  };

  return (
    <section
      data-testid="student-parents-section"
      className="rounded-lg border border-gray-200 bg-white"
    >
      <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div>
          <h3 className="font-medium text-gray-900">Parents &amp; guardians</h3>
          <p className="text-sm text-gray-500">
            {isLoading
              ? 'Loading…'
              : parents.length === 1
              ? '1 linked parent'
              : `${parents.length} linked parents`}
          </p>
        </div>
        {isAdmin && !isAdding && (
          <button
            type="button"
            data-testid="student-parents-add-btn"
            onClick={() => setIsAdding(true)}
            className="shrink-0 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Add parent
          </button>
        )}
      </header>

      {isAdding && (
        <LinkParentForm
          testIdPrefix="student-parents-add"
          submitLabel="Add parent"
          onSubmit={handleAdd}
          onCancel={() => setIsAdding(false)}
        />
      )}

      {parents.length === 0 ? (
        <p data-testid="student-parents-empty" className="px-4 py-6 text-sm text-gray-500">
          {isLoading
            ? 'Loading…'
            : isAdmin
            ? 'No parent has been linked to this child yet. Use "Add parent" to link one by email.'
            : 'No parent has been linked to this child yet. Contact details appear here once an admin links one and the parent creates their account.'}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {parents.map((parent) => (
            <ParentRow
              key={parent.id}
              studentId={studentId}
              parent={parent}
              expanded={expandedId === parent.id}
              onToggle={() =>
                setExpandedId((id) => {
                  const next = id === parent.id ? null : parent.id;
                  // Collapsing (or switching to a different parent's row)
                  // always exits edit mode too — never leave a hidden,
                  // half-filled edit form for another row.
                  if (next !== parent.id) setEditingId((e) => (e === parent.id ? null : e));
                  return next;
                })
              }
              isAdmin={isAdmin}
              isEditing={editingId === parent.id}
              onStartEdit={() => {
                setEditingId(parent.id);
                setExpandedId(parent.id);
              }}
              onStopEdit={() => setEditingId(null)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function ParentRow({
  studentId,
  parent,
  expanded,
  onToggle,
  isAdmin,
  isEditing,
  onStartEdit,
  onStopEdit,
}: {
  studentId: string;
  parent: ParentSummary;
  expanded: boolean;
  onToggle: () => void;
  isAdmin: boolean;
  isEditing: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
}) {
  const name = `${parent.firstName} ${parent.lastName}`.trim();
  const initials = `${parent.firstName[0] ?? ''}${parent.lastName[0] ?? ''}`.toUpperCase();
  // A name is written only by invite-signup, so no name means "invited, has
  // not created their account yet".
  const isPendingSignup = !name;

  return (
    <li>
      <div className="flex w-full items-center gap-3 px-4 py-3 hover:bg-gray-50">
        <button
          type="button"
          data-testid={`student-parent-row-${parent.id}`}
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-medium text-gray-600">
            {initials || '?'}
          </span>
          <span className="min-w-0">
            <span className="flex items-center gap-2">
              <span className="truncate text-sm font-medium text-gray-900">
                {name || parent.email}
              </span>
              {isPendingSignup && (
                <span
                  data-testid={`student-parent-pending-${parent.id}`}
                  className="shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700"
                >
                  Invite sent
                </span>
              )}
            </span>
            <span className="block truncate text-sm text-gray-500">
              {parent.relationship || 'Parent'}
              {parent.isPrimary && ' · Primary'}
              {parent.emergencyContact && ' · Emergency contact'}
            </span>
          </span>
        </button>
        {isAdmin && expanded && !isEditing && (
          <button
            type="button"
            data-testid={`student-parent-edit-btn-${parent.id}`}
            onClick={onStartEdit}
            className="shrink-0 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Edit
          </button>
        )}
      </div>

      {expanded && !isEditing && (
        <dl
          data-testid={`student-parent-card-${parent.id}`}
          className="space-y-3 border-t border-gray-100 bg-gray-50 px-4 py-3 text-sm"
        >
          <ContactRow
            testId={`student-parent-phone-${parent.id}`}
            label="Phone"
            icon={<PhoneIcon className="h-4 w-4 text-gray-400" />}
            value={parent.phone}
            href={parent.phone ? `tel:${parent.phone}` : undefined}
          />
          <ContactRow
            testId={`student-parent-email-${parent.id}`}
            label="Email"
            icon={<EnvelopeIcon className="h-4 w-4 text-gray-400" />}
            value={parent.email}
            href={parent.email ? `mailto:${parent.email}` : undefined}
          />
          <ContactRow
            testId={`student-parent-address-${parent.id}`}
            label="Address"
            icon={<MapPinIcon className="h-4 w-4 text-gray-400" />}
            value={parent.address}
          />
          <div className="flex items-start gap-2">
            <span className="mt-0.5 h-4 w-4" aria-hidden="true" />
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-400">Relationship</dt>
              <dd className="text-gray-900">{parent.relationship || 'Parent'}</dd>
            </div>
          </div>
        </dl>
      )}

      {expanded && isEditing && (
        <ParentContactEditForm studentId={studentId} parent={parent} onDone={onStopEdit} />
      )}

      {expanded && !isEditing && isAdmin && !parent.isPrimary && (
        <RemoveParentControl studentId={studentId} parent={parent} />
      )}
    </li>
  );
}

/**
 * Two-step unlink: "Remove" reveals a confirm line, so a mis-tap on a phone
 * cannot drop a guardian. Not rendered for the primary parent — the backend
 * refuses that anyway (409 PRIMARY_PARENT), and offering a button that always
 * fails would be worse than no button.
 */
function RemoveParentControl({ studentId, parent }: { studentId: string; parent: ParentSummary }) {
  const [isConfirming, setIsConfirming] = useState(false);
  const [removeParent, { isLoading: isRemoving }] = useRemoveStudentParentMutation();
  const { showError, showSuccess } = useToast();
  const name = `${parent.firstName} ${parent.lastName}`.trim() || parent.email;

  const handleRemove = async () => {
    try {
      const { placeholderRemoved } = await removeParent({
        studentId,
        parentId: parent.id,
      }).unwrap();
      // A never-signed-up placeholder is deleted outright with its last link —
      // say so, because "Resend invite" is no longer a thing for them.
      showSuccess(
        placeholderRemoved
          ? `${name} was unlinked. They had not created an account, so their pending invite was deleted.`
          : `${name} no longer has access to this child.`
      );
    } catch (err) {
      // The backend's own wording (primary refused, not linked) beats a
      // generic "try again" that would be wrong for both of those.
      showError(messageFrom(err));
      setIsConfirming(false);
    }
  };

  return (
    <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-4 py-2 text-xs">
      {isConfirming ? (
        <>
          <span className="text-gray-600">Remove {name}? They will lose access to this child.</span>
          <button
            type="button"
            data-testid={`student-parent-remove-cancel-${parent.id}`}
            onClick={() => setIsConfirming(false)}
            disabled={isRemoving}
            className="rounded-md border border-gray-300 px-2.5 py-1 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Keep
          </button>
          <button
            type="button"
            data-testid={`student-parent-remove-confirm-${parent.id}`}
            onClick={handleRemove}
            disabled={isRemoving}
            className="rounded-md bg-red-600 px-2.5 py-1 font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isRemoving ? 'Removing…' : 'Remove'}
          </button>
        </>
      ) : (
        <button
          type="button"
          data-testid={`student-parent-remove-btn-${parent.id}`}
          onClick={() => setIsConfirming(true)}
          className="font-medium text-red-600 hover:text-red-700"
        >
          Remove from this child
        </button>
      )}
    </div>
  );
}

/**
 * Phone/address edit form for one parent row. Email is shown but disabled —
 * it's the sign-in identity, never editable here. A blank phone/address
 * clears that field (matches the parent's own self-edit semantics on
 * /settings/profile, where '' means "remove this").
 */
function ParentContactEditForm({
  studentId,
  parent,
  onDone,
}: {
  studentId: string;
  parent: ParentSummary;
  onDone: () => void;
}) {
  const [phone, setPhone] = useState(parent.phone ?? '');
  const [address, setAddress] = useState(parent.address ?? '');
  const [updateContact, { isLoading: isSaving }] = useUpdateStudentParentContactMutation();
  const { showError, showSuccess } = useToast();

  const handleSave = async () => {
    try {
      await updateContact({ studentId, parentId: parent.id, phone, address }).unwrap();
      showSuccess('Contact details updated.');
      onDone();
    } catch {
      showError('Could not save the contact details. Please try again.');
    }
  };

  return (
    <div
      data-testid={`student-parent-edit-form-${parent.id}`}
      className="space-y-3 border-t border-gray-100 bg-gray-50 px-4 py-3 text-sm"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-gray-400">Phone</span>
          <input
            type="text"
            data-testid={`student-parent-edit-phone-${parent.id}`}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-gray-400">Email</span>
          <input
            type="text"
            disabled
            value={parent.email}
            title="Email is the sign-in identity and cannot be changed here."
            className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 text-gray-500 shadow-sm sm:text-sm"
          />
        </label>
      </div>
      <label className="block">
        <span className="text-xs uppercase tracking-wide text-gray-400">Address</span>
        <input
          type="text"
          data-testid={`student-parent-edit-address-${parent.id}`}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
        />
      </label>
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          data-testid={`student-parent-edit-cancel-${parent.id}`}
          onClick={onDone}
          disabled={isSaving}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid={`student-parent-edit-save-${parent.id}`}
          onClick={handleSave}
          disabled={isSaving}
          className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {isSaving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

/**
 * One labelled contact line. An absent value renders a muted "Not provided"
 * rather than a blank — most parents have recorded neither phone nor address
 * yet, so this is the common case, and a blank row reads as a rendering bug.
 */
function ContactRow({
  testId,
  label,
  icon,
  value,
  href,
}: {
  testId: string;
  label: string;
  icon: React.ReactNode;
  value?: string;
  href?: string;
}) {
  return (
    <div data-testid={testId} className="flex items-start gap-2">
      <span className="mt-0.5">{icon}</span>
      <div className="min-w-0">
        <dt className="text-xs uppercase tracking-wide text-gray-400">{label}</dt>
        <dd className="break-words text-gray-900">
          {value ? (
            href ? (
              <a className="text-primary-600 hover:underline" href={href}>
                {value}
              </a>
            ) : (
              value
            )
          ) : (
            <span className="text-gray-400">Not provided</span>
          )}
        </dd>
      </div>
    </div>
  );
}
